import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import Api from '../Api';
import { AuthService } from '../services/auth.service';
import { ConfigService } from '../config/config.service';
import { getApiModule } from '../apiFactory';
import { ApiModules } from '../ApiModules';
import ErrorHandler from '../services/ErrorHandler';

/**
 * Context type definition, providing typings for the context value.
 */
interface ApiContextType {
  api: Api | null;
  isLoading: boolean;
  error: Error | null;
  getModule: <T extends keyof ApiModules>(moduleName: T) => Promise<ApiModules[T]>;
}

/**
 * Provides a context for accessing the API client and dynamically loaded API modules.
 * This enables components throughout the application to perform API operations
 * without directly managing the API client lifecycle.
 *
 * Usage:
 * const { api, getModule } = useApi();
 * const projectApi = getModule('ProjectApi');
 */
const ApiContext = createContext<ApiContextType>({
  api: null,
  isLoading: true,
  error: null,
  getModule: () => { throw new Error("ApiContext not initialized"); }
});
/**
 * ApiProvider is a React component that initializes the API and provides it globally.
 * It ensures that API client is initialized and provides a method to access API modules dynamically.
 * 
 * @param {ReactNode} children - The children elements to be rendered within this provider.
 */
export const ApiProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [api, setApi] = useState<Api | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initApi = async () => {
      try {
        const authService = new AuthService(process.env.REACT_APP_CLIENT_ID ? process.env.REACT_APP_CLIENT_ID : '');
        const configService = new ConfigService();
        const apiClient = new Api(authService, configService);
        await apiClient.init();
        setApi(apiClient);
      } catch (error) {
        ErrorHandler.logError(new Error("Failed to initialize API client"));
      } finally {
        setLoading(false);
      }
    };

    initApi();
  }, []);

  const getModule = useMemo(() => <T extends keyof ApiModules>(moduleName: T): Promise<ApiModules[T]> => {
    if (!api) throw ErrorHandler.logError(new Error("API client is not initialized"));
    return getApiModule(moduleName, api);
  }, [api]);

  return (
    <ApiContext.Provider value={{ api, isLoading, error, getModule }}>
      {children}
    </ApiContext.Provider>
  );
};

export function
  useApi() {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi must be used within a ApiProvider');
  }
  return context;
}

